import { AccordionPaneImpression } from "@jmc/core/src/components/AccordionImpression";
import { Accordion } from "@jmc/solid-design-system/src/components/molecules/Accordion/Accordion";
import cleanCSSIdentifier from "@jmc/utils/utils/clean-css-identifier";
import { ApplicationState } from "@redux/modules/index";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

interface ItemObject {
    title: string | Element | JSX.Element;
    titleString?: string;
    content: React.ReactNode[];
}

interface Props {
    items: ItemObject[];
    type?: "none" | "firstOpen" | "onlyOne";
    placement?: string;
    titleComponent?: string;
}

export const Concertina = ({ items, type = "none", placement = "none", titleComponent = null }: Props): JSX.Element => {
    const initialPaneStates: boolean[] = [];
    for (let i = 0; i < items.length; i++) {
        if (i === 0 && type === "firstOpen") {
            initialPaneStates.push(true);
        } else {
            initialPaneStates.push(false);
        }
    }
    const [panes, updatePanes] = useState(initialPaneStates);
    const paneId = useSelector((state: ApplicationState) => state.panes.accordionPaneId);

    useEffect(() => {
        window.addEventListener("hashchange", onHashChange);

        return (): void => {
            window.removeEventListener("hashchange", onHashChange);
        };
    });

    useEffect(() => {
        if (typeof window !== `undefined` && window?.location?.hash) {
            onHashChange();
        }
    }, []);

    // Verify if paneId is available for referencing footnotes
    useEffect(() => {
        if (typeof window !== `undefined` && window?.location?.hash && paneId) {
            onHashChange();
        }
    }, [paneId]);

    const filterAccordionOnHash = (paneTitle: string): number => {
        const filteredAccordion = items?.findIndex((pane) => {
            return `Accordion.${cleanCSSIdentifier(pane?.titleString)}` === paneTitle;
        });
        return filteredAccordion;
    };

    const onHashChange = (): void => {
        if (typeof window !== `undefined`) {
            const hash = decodeURI(window?.location?.hash?.replace("#", ""));
            if (filterAccordionOnHash(hash) > -1) {
                const currentAccordionElement = document?.getElementById(hash);
                const currentIndex = currentAccordionElement?.getAttribute("data-tracking-index");
                toggleVisibility(parseInt(currentIndex), true);
                currentAccordionElement?.focus();
            } else {
                const closestAccordionElement = document?.getElementById(hash)?.closest("details");
                const closestAccordionId = closestAccordionElement?.getAttribute("id");
                const closestSummaryElement = closestAccordionElement?.querySelector("summary");
                const closestSummaryId = closestSummaryElement?.getAttribute("id");
                if (
                    filterAccordionOnHash(closestSummaryId) > -1 &&
                    `${placement}-${closestSummaryId}` === closestAccordionId
                ) {
                    const currentIndex = closestSummaryElement?.getAttribute("data-tracking-index");
                    toggleVisibility(parseInt(currentIndex), true);
                }
            }
        }
    };

    const toggleVisibility = (id: number, active?: boolean): void => {
        if (type === "onlyOne") {
            const originalValue = panes[id];
            const newPanes: boolean[] = [];
            for (let i = 0; i < panes.length; i++) {
                newPanes.push(false);
            }
            newPanes[id] = active ? true : !originalValue;
            updatePanes(newPanes);
        } else {
            const newPanes = [...panes];
            newPanes[id] = active ? true : !newPanes[id];
            updatePanes(newPanes);
        }
    };

    return (
        <div>
            {items.map(({ content, title, titleString }: ItemObject, index: number) => {
                const titleAsString = titleString
                    ? titleString
                    : title && typeof title === "string"
                    ? title?.toLowerCase()
                    : "";

                return (
                    <AccordionPaneImpression key={index} name={titleString} open={panes[index]}>
                        <Accordion
                            key={index}
                            id={index.toString()}
                            open={panes[index]}
                            title={title}
                            color="title"
                            onClick={toggleVisibility}
                            backgroundColor="white"
                            autoId={`Accordion.${cleanCSSIdentifier(titleAsString)}`}
                            detailsId={`${placement}-Accordion.${cleanCSSIdentifier(titleAsString)}`}
                            titleComponent={titleComponent}
                        >
                            <div>{content}</div>
                        </Accordion>
                    </AccordionPaneImpression>
                );
            })}
        </div>
    );
};
export default Concertina;
